/**
 * NURBS kernel — higher surface operations (R2): vertical ray projection,
 * parameter knot refinement, normal offset, edge extension, trim masks,
 * tween/match and arc-length unroll. Pure functions over the serializable
 * surface payloads; runners and tools share these.
 */

import type { Pt3 } from '../../commands/geometryMath';
import type { NurbsSurfaceData } from './types';
import { surfaceDomains } from './types';
import { evaluateSurface, surfaceDerivatives, surfaceFromControlNet } from './surface';

/** Domain corner helper. */
export function domain(data: NurbsSurfaceData): { u: [number, number]; v: [number, number] } {
    return surfaceDomains(data);
}

/** Trim mask grid resolution (matches the default tessellation grade). */
export const TRIM_GRID = 24;

/**
 * Solve S(u,v) = (x, y, ·) for the vertical line at (x, y): 2D Newton on
 * the (Su, Sv) XY projection, multi-start over a parameter grid; returns
 * the closest hit or null when the ray misses the surface.
 */
export function surfaceRayZ(
    data: NurbsSurfaceData, x: number, y: number
): { u: number; v: number; point: Pt3 } | null {
    const { u: [u0, u1], v: [v0, v1] } = domain(data);
    const grid = 8;
    let best: { u: number; v: number; point: Pt3; d: number } | null = null;
    for (let i = 0; i <= grid; i++) {
        for (let j = 0; j <= grid; j++) {
            let u = u0 + ((u1 - u0) * i) / grid;
            let v = v0 + ((v1 - v0) * j) / grid;
            for (let it = 0; it < 20; it++) {
                const { point, Su, Sv } = surfaceDerivatives(data, u, v);
                const rx = point[0] - x, ry = point[1] - y;
                if (Math.hypot(rx, ry) < 1e-9) break;
                const det = Su[0] * Sv[1] - Su[1] * Sv[0];
                if (Math.abs(det) < 1e-12) break;
                const du = (-rx * Sv[1] + ry * Su[1]) / det;
                const dv = (-Su[0] * ry + Sv[0] * rx) / det;
                u = Math.min(Math.max(u + du, u0), u1);
                v = Math.min(Math.max(v + dv, v0), v1);
                if (Math.hypot(du, dv) < 1e-11) break;
            }
            const p = evaluateSurface(data, u, v);
            const d = Math.hypot(p[0] - x, p[1] - y);
            if (d < 1e-5 && (!best || d < best.d)) best = { u, v, point: p, d };
        }
    }
    if (!best) return null;
    const { u, v, point } = best;
    return { u, v, point };
}

/** Binary span search (A2.1) — local so ops stays self-contained. */
function span(n: number, degree: number, t: number, knots: number[]): number {
    if (t >= knots[n + 1]) return n;
    if (t <= knots[degree]) return degree;
    let low = degree, high = n + 1, mid = Math.floor((low + high) / 2);
    while (t < knots[mid] || t >= knots[mid + 1]) {
        if (t < knots[mid]) high = mid; else low = mid;
        mid = Math.floor((low + high) / 2);
    }
    return mid;
}

/**
 * Insert one knot along `axis` (Boehm, homogeneous — handles rational
 * weights). Shape-exact; CV count on that axis grows by one.
 */
function insertKnotAxis(data: NurbsSurfaceData, param: number, axis: 'u' | 'v'): NurbsSurfaceData {
    const knots = axis === 'u' ? data.knotsU : data.knotsV;
    const degree = axis === 'u' ? data.degreeU : data.degreeV;
    const rational = !!data.weights;
    const rows: Pt3[][] = axis === 'u' ? data.cv : transposeGrid(data.cv);
    const weightRows: number[][] | null = rational
        ? (axis === 'u' ? data.weights! : transposeGridNums(data.weights!))
        : null;
    const n = rows[0].length - 1;
    const k = span(n, degree, param, knots);
    const outRows: [number, number, number, number][][] = rows.map((row, ri) => {
        const wrow = weightRows ? weightRows[ri] : null;
        const hom = row.map((pt, i) => {
            const w = wrow ? wrow[i] : 1;
            return [pt[0] * w, pt[1] * w, pt[2] * w, w] as [number, number, number, number];
        });
        const res: [number, number, number, number][] = [];
        for (let i = 0; i <= k - degree; i++) res.push(hom[i]);
        for (let i = k - degree + 1; i <= k; i++) {
            const denom = knots[i + degree] - knots[i];
            const alpha = denom === 0 ? 0 : (param - knots[i]) / denom;
            const a = hom[i], b = hom[i - 1];
            res.push([
                b[0] + alpha * (a[0] - b[0]), b[1] + alpha * (a[1] - b[1]),
                b[2] + alpha * (a[2] - b[2]), b[3] + alpha * (a[3] - b[3]),
            ]);
        }
        for (let i = k; i <= n; i++) res.push(hom[i]);
        return res;
    });
    const newKnots = [...knots];
    newKnots.splice(k + 1, 0, param);
    const dehom: Pt3[][] = outRows.map((row) => row.map((h) =>
        [h[3] === 0 ? 0 : h[0] / h[3], h[3] === 0 ? 0 : h[1] / h[3], h[3] === 0 ? 0 : h[2] / h[3]] as Pt3
    ));
    const dehomW: number[][] = outRows.map((row) => row.map((h) => h[3]));
    if (axis === 'u') {
        return {
            ...data,
            cv: dehom,
            knotsU: newKnots,
            weights: rational ? dehomW : undefined,
        };
    }
    return {
        ...data,
        cv: transposeGrid(dehom),
        knotsV: newKnots,
        weights: rational ? transposeGridNums(dehomW) : undefined,
    };
}

/** Insert knots in a regular grid along both axes (for SplitEdge/trim). */
export function refineSurfaceGrid(data: NurbsSurfaceData, count = 4): NurbsSurfaceData {
    const { u, v } = domain(data);
    let out = data;
    for (let i = 1; i < count; i++) {
        out = insertKnotAxis(out, u[0] + ((u[1] - u[0]) * i) / count, 'u');
        out = insertKnotAxis(out, v[0] + ((v[1] - v[0]) * i) / count, 'v');
    }
    return out;
}

/** Insert one knot at the split parameter — the SplitEdge primitive. */
export function splitSurfaceEdge(data: NurbsSurfaceData, edgeIndex: 0 | 1 | 2 | 3, t: number): NurbsSurfaceData {
    const { u, v } = domain(data);
    // edges 0 (v-min) & 2 (v-max) split along U; 1 (u-max) & 3 (u-min) along V
    if (edgeIndex === 0 || edgeIndex === 2) {
        const uu = u[0] + (u[1] - u[0]) * t;
        return insertKnotAxis(data, uu, 'u');
    }
    const vv = v[0] + (v[1] - v[0]) * t;
    return insertKnotAxis(data, vv, 'v');
}

function transposeGrid(m: Pt3[][]): Pt3[][] {
    const out: Pt3[][] = [];
    const cols = m[0]?.length ?? 0;
    for (let c = 0; c < cols; c++) out.push(m.map((row) => row[c]));
    return out;
}
function transposeGridNums(m: number[][]): number[][] {
    const out: number[][] = [];
    const cols = m[0]?.length ?? 0;
    for (let c = 0; c < cols; c++) out.push(m.map((row) => row[c]));
    return out;
}

/** Normal-offset copy of the surface along its own normals by distance. */
export function offsetSurfaceData(data: NurbsSurfaceData, distance: number): NurbsSurfaceData {
    const { u, v } = domain(data);
    const rows = data.cv.length, cols = data.cv[0].length;
    const cv = data.cv.map((row, vi) => row.map((p, ui) => {
        const vv = rows > 1 ? v[0] + ((v[1] - v[0]) * vi) / (rows - 1) : v[0];
        const uu = cols > 1 ? u[0] + ((u[1] - u[0]) * ui) / (cols - 1) : u[0];
        const n = surfaceDerivatives(data, uu, vv).normal;
        return [p[0] + n[0] * distance, p[1] + n[1] * distance, (p[2] ?? 0) + n[2] * distance] as Pt3;
    }));
    return { ...data, cv };
}

/** Per-region variable offset: thickness varies along U from dStart to dEnd. */
export function offsetSurfaceVariable(data: NurbsSurfaceData, dStart: number, dEnd: number): NurbsSurfaceData {
    const { u, v } = domain(data);
    const rows = data.cv.length, cols = data.cv[0].length;
    const cv = data.cv.map((row, vi) => row.map((p, ui) => {
        const vv = rows > 1 ? v[0] + ((v[1] - v[0]) * vi) / (rows - 1) : v[0];
        const uu = cols > 1 ? u[0] + ((u[1] - u[0]) * ui) / (cols - 1) : u[0];
        const d = dStart + (dEnd - dStart) * (cols > 1 ? ui / (cols - 1) : 0);
        const n = surfaceDerivatives(data, uu, vv).normal;
        return [p[0] + n[0] * d, p[1] + n[1] * d, (p[2] ?? 0) + n[2] * d] as Pt3;
    }));
    return { ...data, cv };
}

/** Grow the patch by one control ring on every side, continuing the edge
 *  direction (tangent-continuous extension). */
export function extendSurfaceData(data: NurbsSurfaceData, distance: number): NurbsSurfaceData {
    const rows = data.cv.length, cols = data.cv[0].length;
    const dir = (a: Pt3, b: Pt3, dist: number): Pt3 => {
        const d = [a[0] - b[0], a[1] - b[1], (a[2] ?? 0) - (b[2] ?? 0)];
        const l = Math.hypot(...d) || 1;
        return [a[0] + (d[0] / l) * dist, a[1] + (d[1] / l) * dist, (a[2] ?? 0) + (d[2] / l) * dist] as Pt3;
    };
    const top = data.cv[0].map((p, i) => dir(p, data.cv[Math.min(1, rows - 1)][i], distance));
    const bottom = data.cv[rows - 1].map((p, i) => dir(p, data.cv[Math.max(0, rows - 2)][i], distance));
    let cv = [top, ...data.cv.map((r) => [...r]), bottom];
    cv = cv.map((row, ri) => {
        const left = dir(row[0], row[Math.min(1, cols - 1)], distance);
        const right = dir(row[cols - 1], row[Math.max(0, cols - 2)], distance);
        return [left, ...row, right];
    });
    const out = surfaceFromControlNet(cv, data.degreeU, data.degreeV);
    return out ? { ...out, trims: data.trims } : data;
}

/**
 * Trim by cutter curve + keep point, in parameter space via a grid flood
 * mask (robust, exact-enough at TRIM_GRID resolution). The cutter is
 * projected onto the surface (point inversion), rasterized onto the
 * parameter grid, and the connected keep-region around the picked point
 * becomes `data.trimMask`. Returns false when the cutter misses.
 */
export function applyTrimMask(
    data: NurbsSurfaceData, cutter3d: Pt3[], keepUV: [number, number]
): { surface: NurbsSurfaceData; ok: boolean } {
    const { u: [u0, u1], v: [v0, v1] } = domain(data);
    const n = TRIM_GRID;
    // rasterize the cutter polyline onto the grid
    const wall: boolean[][] = Array.from({ length: n + 1 }, () => new Array(n + 1).fill(false));
    let hits = 0;
    const toIdx = (uv: [number, number]): [number, number] | null => {
        const i = Math.round(((uv[0] - u0) / (u1 - u0 || 1)) * n);
        const j = Math.round(((uv[1] - v0) / (v1 - v0 || 1)) * n);
        if (i < 0 || i > n || j < 0 || j > n) return null;
        return [i, j];
    };
    for (let s = 0; s < cutter3d.length - 1; s++) {
        const a = cutter3d[s], b = cutter3d[s + 1];
        const steps = 8;
        for (let k = 0; k <= steps; k++) {
            const p: Pt3 = [a[0] + (b[0] - a[0]) * (k / steps), a[1] + (b[1] - a[1]) * (k / steps), a[2] + (b[2] - a[2]) * (k / steps)];
            const idx = toIdx(projectToUV(data, p));
            if (idx) { wall[idx[1]][idx[0]] = true; hits++; }
        }
    }
    if (hits < 2) return { surface: data, ok: false };
    // flood fill from the keep point; walls block
    const seed = toIdx(keepUV);
    if (!seed || wall[seed[1]][seed[0]]) return { surface: data, ok: false };
    const keep: boolean[][] = Array.from({ length: n + 1 }, () => new Array(n + 1).fill(false));
    const stack = [seed];
    while (stack.length) {
        const [i, j] = stack.pop()!;
        if (keep[j][i] || wall[j][i]) continue;
        keep[j][i] = true;
        if (i > 0) stack.push([i - 1, j]);
        if (i < n) stack.push([i + 1, j]);
        if (j > 0) stack.push([i, j - 1]);
        if (j < n) stack.push([i, j + 1]);
    }
    const kept = keep.flat().filter(Boolean).length;
    const total = (n + 1) * (n + 1);
    if (kept === total) return { surface: data, ok: false }; // nothing cut
    return {
        surface: { ...data, trimMask: keep },
        ok: true,
    };
}

/** Untrim: drop the mask, restore the full patch. */
export function untrimSurface(data: NurbsSurfaceData): NurbsSurfaceData {
    const { trimMask, ...rest } = data;
    void trimMask;
    return rest as NurbsSurfaceData;
}

/** World point (u,v) of the surface for an arbitrary 3D probe. */
function projectToUV(data: NurbsSurfaceData, p: Pt3): [number, number] {
    const { u: [u0, u1], v: [v0, v1] } = domain(data);
    let bu = (u0 + u1) / 2, bv = (v0 + v1) / 2, bd = Infinity;
    const grid = 10;
    for (let i = 0; i <= grid; i++) {
        for (let j = 0; j <= grid; j++) {
            const u = u0 + ((u1 - u0) * i) / grid;
            const v = v0 + ((v1 - v0) * j) / grid;
            const q = evaluateSurface(data, u, v);
            const d = Math.hypot(q[0] - p[0], q[1] - p[1], q[2] - p[2]);
            if (d < bd) { bd = d; bu = u; bv = v; }
        }
    }
    return [bu, bv];
}

/**
 * ShrinkTrim: reparameterize the kept region of a trimmed surface onto a
 * fresh control net — the honest functional equivalent of collapsing the
 * unused control hull. When no mask exists the surface is returned
 * unchanged and `changed` is false.
 */
export function shrinkTrimmedSurface(data: NurbsSurfaceData): { surface: NurbsSurfaceData; changed: boolean } {
    if (!data.trimMask) return { surface: data, changed: false };
    const n = TRIM_GRID;
    const { u: [u0, u1], v: [v0, v1] } = domain(data);
    let i0 = n, i1 = 0, j0 = n, j1 = 0, any = false;
    for (let j = 0; j <= n; j++) for (let i = 0; i <= n; i++) {
        if (!data.trimMask[j][i]) continue;
        any = true;
        i0 = Math.min(i0, i); i1 = Math.max(i1, i); j0 = Math.min(j0, j); j1 = Math.max(j1, j);
    }
    if (!any || i1 <= i0 || j1 <= j0) return { surface: data, changed: false };
    // kept parameter box → resample into a fresh 9x9 net (shrink the hull)
    const bu0 = u0 + ((u1 - u0) * i0) / n, bu1 = u0 + ((u1 - u0) * i1) / n;
    const bv0 = v0 + ((v1 - v0) * j0) / n, bv1 = v0 + ((v1 - v0) * j1) / n;
    const m = 8;
    const cv: Pt3[][] = [];
    for (let j = 0; j <= m; j++) {
        const row: Pt3[] = [];
        for (let i = 0; i <= m; i++) {
            const u = bu0 + ((bu1 - bu0) * i) / m;
            const v = bv0 + ((bv1 - bv0) * j) / m;
            row.push(evaluateSurface(data, u, v));
        }
        cv.push(row);
    }
    const fit = surfaceFromControlNet(cv);
    if (!fit) return { surface: data, changed: false };
    return {
        surface: { ...fit, trims: undefined, trimMask: undefined, planar: data.planar },
        changed: true,
    };
}

/** Arc-length corrected unroll to the XY plane (developable-exact). */
export function unrollSurfaceToPlane(
    data: NurbsSurfaceData
): { cv: Pt3[][]; stretchError: number } {
    const rows = 9, cols = 9;
    const { u, v } = domain(data);
    const vLen: number[] = [];
    for (let j = 0; j < rows; j++) {
        let sum = 0;
        const vv = v[0] + ((v[1] - v[0]) * j) / (rows - 1);
        let prev = evaluateSurface(data, u[0], vv);
        for (let i = 1; i < cols; i++) {
            const uu = u[0] + ((u[1] - u[0]) * i) / (cols - 1);
            const p = evaluateSurface(data, uu, vv);
            sum += Math.hypot(p[0] - prev[0], p[1] - prev[1], p[2] - prev[2]);
            prev = p;
        }
        vLen.push(sum);
    }
    const uLen: number[] = [];
    for (let i = 0; i < cols; i++) {
        let sum = 0;
        const uu = u[0] + ((u[1] - u[0]) * i) / (cols - 1);
        let prev = evaluateSurface(data, uu, v[0]);
        for (let j = 1; j < rows; j++) {
            const vv = v[0] + ((v[1] - v[0]) * j) / (rows - 1);
            const p = evaluateSurface(data, uu, vv);
            sum += Math.hypot(p[0] - prev[0], p[1] - prev[1], p[2] - prev[2]);
            prev = p;
        }
        uLen.push(sum);
    }
    const avgU = vLen.reduce((a, b) => a + b, 0) / vLen.length;
    const avgV = uLen.reduce((a, b) => a + b, 0) / uLen.length;
    const cv: Pt3[][] = [];
    for (let j = 0; j < rows; j++) {
        const row: Pt3[] = [];
        for (let i = 0; i < cols; i++) row.push([(avgU * i) / (cols - 1), (avgV * j) / (rows - 1), 0]);
        cv.push(row);
    }
    let err = 0;
    vLen.forEach((l) => { err += Math.abs(l - avgU) / (avgU || 1); });
    err /= vLen.length;
    return { cv, stretchError: err };
}

/** Tween two surfaces by factor t, sampled on a common parameter grid. */
export function tweenSurface(a: NurbsSurfaceData, b: NurbsSurfaceData, t: number): NurbsSurfaceData | null {
    const rows = 9, cols = 9;
    const cv: Pt3[][] = [];
    const da = domain(a), db = domain(b);
    for (let j = 0; j < rows; j++) {
        const row: Pt3[] = [];
        for (let i = 0; i < cols; i++) {
            const v0 = j / (rows - 1), u0 = i / (cols - 1);
            const pa = evaluateSurface(a, da.u[0] + (da.u[1] - da.u[0]) * u0, da.v[0] + (da.v[1] - da.v[0]) * v0);
            const pb = evaluateSurface(b, db.u[0] + (db.u[1] - db.u[0]) * u0, db.v[0] + (db.v[1] - db.v[0]) * v0);
            row.push([pa[0] + (pb[0] - pa[0]) * t, pa[1] + (pb[1] - pa[1]) * t, pa[2] + (pb[2] - pa[2]) * t]);
        }
        cv.push(row);
    }
    return surfaceFromControlNet(cv);
}

/**
 * MatchSrf G1: extrapolate the target's rows adjacent to the shared edge
 * along the source's boundary tangents (linearized tangent continuity).
 * `edge` selects the target's boundary (0 = v-min, 2 = v-max) being
 * matched: its outer CV row is mirrored across the neighbor row, which
 * makes the boundary tangent continuous with a coplanar source edge.
 */
export function matchSurfaceG1(source: NurbsSurfaceData, target: NurbsSurfaceData, edge: 0 | 1 | 2 | 3): NurbsSurfaceData {
    void source;
    const cv = target.cv.map((r) => [...r] as Pt3[]);
    if (edge === 2 && cv.length >= 2) {
        const last = cv[cv.length - 1], second = cv[cv.length - 2];
        cv[cv.length - 1] = last.map((p, i) => [2 * p[0] - second[i][0], 2 * p[1] - second[i][1], 2 * (p[2] ?? 0) - (second[i][2] ?? 0)] as Pt3);
    } else if (edge === 0 && cv.length >= 2) {
        const last = cv[0], second = cv[1];
        cv[0] = last.map((p, i) => [2 * p[0] - second[i][0], 2 * p[1] - second[i][1], 2 * (p[2] ?? 0) - (second[i][2] ?? 0)] as Pt3);
    }
    return { ...target, cv };
}
